import React, { useEffect, useRef, useState } from "react";
import injectSheet from "react-jss";

import TourCanvasMinimap from "../TourCanvasMinimap";
import FiltersPanel from "./FiltersPanel";
import { matchesStudioFilter } from "./imageFilter";
import { BOARD_LIMIT, gridUnit } from "../TourCanvas";

/**
 * StudioInspector — the right-hand panel of TouchTour Studio (WP-B).
 *
 * It owns the read-only "view" controls that used to float over the canvas:
 *   - the FiltersPanel (WP-E): status / floor / tag filters,
 *   - the minimap (moved here from its floating position).
 *
 * It never reaches into TourCanvas internals: every camera move goes through
 * the Canvas API held in `canvasApiRef`, and live camera state is read by
 * polling `getCameraState()` on each frame.
 */

const StudioInspector = ({
  canvasApiRef,
  images,
  activeImage,
  filter,
  setFilter,
  floorplans = [],
  floorIndex = {},
  classes,
}) => {
  const [camera, setCamera] = useState({
    x: 0,
    y: 0,
    scale: 1,
    canvasRect: null,
  });

  // The Canvas API has no change events, so poll a snapshot each frame and only
  // re-render when something the panel shows actually moved.
  const cameraRef = useRef(camera);
  useEffect(() => {
    let frame;
    const tick = () => {
      const api = canvasApiRef && canvasApiRef.current;
      if (api) {
        const next = api.getCameraState();
        const prev = cameraRef.current;
        const rectChanged =
          (next.canvasRect && next.canvasRect.width) !==
          (prev.canvasRect && prev.canvasRect.width);
        if (
          next.x !== prev.x ||
          next.y !== prev.y ||
          next.scale !== prev.scale ||
          rectChanged
        ) {
          cameraRef.current = next;
          setCamera(next);
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [canvasApiRef]);

  const callApi = (method, ...args) => {
    const api = canvasApiRef && canvasApiRef.current;
    if (api && typeof api[method] === "function") {
      api[method](...args);
    }
  };

  // The minimap mirrors the canvas: show only the nodes that pass the filter so
  // both views stay in sync (acceptance: filters hide/show nodes on canvas AND
  // minimap).
  const allImages = Array.isArray(images) ? images : [];
  const visibleImages = allImages.filter(image =>
    matchesStudioFilter(image, filter, floorIndex),
  );

  return (
    <div className={classes.root}>
      <Section classes={classes} title="Filters">
        <FiltersPanel
          filter={filter}
          setFilter={setFilter}
          images={allImages}
          floorplans={floorplans}
        />
      </Section>

      <Section classes={classes} title="Mini map">
        <TourCanvasMinimap
          images={visibleImages}
          activeImage={activeImage}
          panX={camera.x}
          panY={camera.y}
          scale={camera.scale}
          canvasRect={camera.canvasRect}
          boardLimit={BOARD_LIMIT}
          gridUnit={gridUnit}
          onJump={(worldX, worldY) => callApi("jumpTo", worldX, worldY)}
        />
      </Section>
    </div>
  );
};

const Section = ({ classes, title, children }) => (
  <div className={classes.section}>
    <div className={classes.sectionTitle}>{title}</div>
    {children}
  </div>
);

const styles = theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    overflowY: "auto",
  },
  section: {
    padding: `${theme.spacing3} ${theme.spacing2}`,
    borderBottom: `1px solid ${theme.colorDarkGrey}`,
  },
  sectionTitle: {
    color: theme.colorGrey,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: theme.spacing2,
  },
});

export default injectSheet(styles)(StudioInspector);
